import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import LockIcon from '@mui/icons-material/Lock';
import CodeIcon from '@mui/icons-material/Code';
import TagIcon from '@mui/icons-material/Tag';
import TranslateIcon from '@mui/icons-material/Translate';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PageHeader from '../components/common/PageHeader';

// ─── Operation definitions ───────────────────────────────────

interface OpDef {
  id: string;
  label: string;
  category: 'encode' | 'decode' | 'hash' | 'transform';
  fn: (input: string) => string;
}

function safeAtob(str: string): string {
  try {
    return atob(str.trim());
  } catch {
    return '[Fehler: Ungültiger Base64-Input]';
  }
}

function hexEncode(str: string): string {
  return Array.from(new TextEncoder().encode(str))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

function hexDecode(str: string): string {
  try {
    const cleaned = str.replace(/[^0-9a-fA-F]/g, '');
    if (cleaned.length % 2 !== 0) return '[Fehler: Ungültige Hex-Länge]';
    const bytes = new Uint8Array(
      cleaned.match(/.{2}/g)!.map((b) => parseInt(b, 16)),
    );
    return new TextDecoder().decode(bytes);
  } catch {
    return '[Fehler: Ungültiger Hex-Input]';
  }
}

function htmlEncode(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

function htmlDecode(str: string): string {
  const el = document.createElement('span');
  el.innerHTML = str;
  return el.textContent || '';
}

function rot13(str: string): string {
  return str.replace(/[a-zA-Z]/g, (c) => {
    const base = c <= 'Z' ? 65 : 97;
    return String.fromCharCode(((c.charCodeAt(0) - base + 13) % 26) + base);
  });
}

async function hashDigest(algo: string, input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest(algo, data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function unicodeEscape(str: string): string {
  return Array.from(str)
    .map((c) => {
      const code = c.codePointAt(0)!;
      return code > 127 ? `\\u${code.toString(16).padStart(4, '0')}` : c;
    })
    .join('');
}

function unicodeUnescape(str: string): string {
  return str.replace(/\\u([0-9a-fA-F]{4,6})/g, (_, hex) =>
    String.fromCodePoint(parseInt(hex, 16)),
  );
}

const SYNC_OPS: OpDef[] = [
  // Encode
  { id: 'base64-enc', label: 'Base64 Encode', category: 'encode', fn: (s) => btoa(unescape(encodeURIComponent(s))) },
  { id: 'base64-dec', label: 'Base64 Decode', category: 'decode', fn: safeAtob },
  { id: 'url-enc', label: 'URL Encode', category: 'encode', fn: encodeURIComponent },
  { id: 'url-dec', label: 'URL Decode', category: 'decode', fn: (s) => { try { return decodeURIComponent(s); } catch { return '[Fehler: Ungültig]'; } } },
  { id: 'html-enc', label: 'HTML Encode', category: 'encode', fn: htmlEncode },
  { id: 'html-dec', label: 'HTML Decode', category: 'decode', fn: htmlDecode },
  { id: 'hex-enc', label: 'Hex Encode', category: 'encode', fn: hexEncode },
  { id: 'hex-dec', label: 'Hex Decode', category: 'decode', fn: hexDecode },
  { id: 'unicode-esc', label: 'Unicode Escape', category: 'encode', fn: unicodeEscape },
  { id: 'unicode-unesc', label: 'Unicode Unescape', category: 'decode', fn: unicodeUnescape },
  // Transform
  { id: 'rot13', label: 'ROT13', category: 'transform', fn: rot13 },
  { id: 'reverse', label: 'Reverse', category: 'transform', fn: (s) => Array.from(s).reverse().join('') },
  { id: 'upper', label: 'To Uppercase', category: 'transform', fn: (s) => s.toUpperCase() },
  { id: 'lower', label: 'To Lowercase', category: 'transform', fn: (s) => s.toLowerCase() },
  { id: 'trim', label: 'Trim Whitespace', category: 'transform', fn: (s) => s.trim() },
  { id: 'length', label: 'String Length', category: 'transform', fn: (s) => String(s.length) },
  { id: 'line-sort', label: 'Sort Lines', category: 'transform', fn: (s) => s.split('\n').sort().join('\n') },
  { id: 'line-uniq', label: 'Unique Lines', category: 'transform', fn: (s) => Array.from(new Set(s.split('\n'))).join('\n') },
  { id: 'line-count', label: 'Count Lines', category: 'transform', fn: (s) => String(s.split('\n').length) },
];

interface AsyncOpDef {
  id: string;
  label: string;
  category: 'hash';
  fn: (input: string) => Promise<string>;
}

const ASYNC_OPS: AsyncOpDef[] = [
  { id: 'md5', label: 'MD5 (nicht verfügbar in Browser)', category: 'hash', fn: async () => '[MD5 nicht nativ im Browser – SHA verwenden]' },
  { id: 'sha1', label: 'SHA-1', category: 'hash', fn: (s) => hashDigest('SHA-1', s) },
  { id: 'sha256', label: 'SHA-256', category: 'hash', fn: (s) => hashDigest('SHA-256', s) },
  { id: 'sha512', label: 'SHA-512', category: 'hash', fn: (s) => hashDigest('SHA-512', s) },
];

type AnyOp = OpDef | AsyncOpDef;

const ALL_OPS: AnyOp[] = [...SYNC_OPS, ...ASYNC_OPS];

const CATEGORY_ICON: Record<string, React.ReactElement> = {
  encode: <CodeIcon fontSize="small" />,
  decode: <DataObjectIcon fontSize="small" />,
  hash: <LockIcon fontSize="small" />,
  transform: <TranslateIcon fontSize="small" />,
};

const CATEGORY_LABEL: Record<string, string> = {
  encode: 'Encode',
  decode: 'Decode',
  hash: 'Hash',
  transform: 'Transform',
};

const CATEGORY_COLOR: Record<string, 'info' | 'success' | 'warning' | 'secondary'> = {
  encode: 'info',
  decode: 'success',
  hash: 'warning',
  transform: 'secondary',
};

// ─── Component ───────────────────────────────────────────────

interface PipelineStep {
  instanceId: number;
  opId: string;
}

let _nextId = 1;

const WorkbenchCyberChefPage: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [pipeline, setPipeline] = useState<PipelineStep[]>([]);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Run pipeline whenever input or pipeline changes
  const computeOutput = useCallback(
    async (text: string, steps: PipelineStep[]) => {
      let result = text;
      for (const step of steps) {
        const op = ALL_OPS.find((o) => o.id === step.opId);
        if (!op) continue;
        try {
          if ('fn' in op) {
            const out = op.fn(result);
            result = out instanceof Promise ? await out : out;
          }
        } catch {
          result = `[Fehler in ${op.label}]`;
          break;
        }
      }
      return result;
    },
    [],
  );

  useMemo(() => {
    computeOutput(input, pipeline).then(setOutput);
  }, [input, pipeline, computeOutput]);

  const addOp = (opId: string) => {
    setPipeline((prev) => [...prev, { instanceId: _nextId++, opId }]);
    setMenuAnchor(null);
  };

  const removeOp = (instanceId: number) => {
    setPipeline((prev) => prev.filter((s) => s.instanceId !== instanceId));
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* blocked */ }
  };

  const handleOutputToInput = () => {
    setInput(output);
    setPipeline([]);
  };

  const grouped = useMemo(() => {
    const groups: Record<string, AnyOp[]> = {};
    for (const op of ALL_OPS) {
      (groups[op.category] ??= []).push(op);
    }
    return groups;
  }, []);

  return (
    <Box>
      <PageHeader
        title="CyberChef Light"
        subtitle="Encoder / Decoder / Hasher – Pipeline zum Verketten von Operationen"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'CyberChef Light' },
        ]}
      />

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
        {/* ── Left: Input ── */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Input</Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {input.length} Zeichen
            </Typography>
          </Box>
          <TextField
            multiline
            minRows={12}
            maxRows={24}
            fullWidth
            placeholder="Text hier einfügen..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                fontSize: '0.85rem',
              },
            }}
          />
        </Paper>

        {/* ── Center: Pipeline ── */}
        <Paper
          sx={{
            width: { xs: '100%', lg: 280 },
            flexShrink: 0,
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pipeline</Typography>
            <Tooltip title="Operation hinzufügen">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {pipeline.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 3, fontStyle: 'italic' }}
            >
              Keine Operationen.{'\n'}Klicke +, um eine hinzuzufügen.
            </Typography>
          )}

          {pipeline.map((step, idx) => {
            const op = ALL_OPS.find((o) => o.id === step.opId);
            if (!op) return null;
            return (
              <Box key={step.instanceId}>
                {idx > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.3 }}>
                    <ArrowDownwardIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Chip
                    label={CATEGORY_LABEL[op.category]}
                    size="small"
                    color={CATEGORY_COLOR[op.category]}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    {op.label}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeOp(step.instanceId)}
                    sx={{ color: theme.palette.error.main, p: 0.3 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}

          {pipeline.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={() => setPipeline([])}
              sx={{ mt: 1, alignSelf: 'center', fontSize: '0.75rem' }}
            >
              Pipeline leeren
            </Button>
          )}

          {/* Add-Operation Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { maxHeight: 400, width: 260 } } }}
          >
            {Object.entries(grouped).map(([cat, ops]) => [
              <Divider key={`div-${cat}`} textAlign="left" sx={{ fontSize: '0.7rem', my: 0.5 }}>
                {CATEGORY_LABEL[cat]}
              </Divider>,
              ...ops.map((op) => (
                <MenuItem key={op.id} onClick={() => addOp(op.id)} dense>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {CATEGORY_ICON[op.category]}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }}>
                    {op.label}
                  </ListItemText>
                </MenuItem>
              )),
            ])}
          </Menu>
        </Paper>

        {/* ── Right: Output ── */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Output</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Output als neuen Input verwenden">
                <IconButton size="small" onClick={handleOutputToInput} disabled={!output}>
                  <SwapVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={copied ? 'Kopiert!' : 'Kopieren'}>
                <IconButton size="small" onClick={() => handleCopy(output)} disabled={!output}>
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              minHeight: 280,
              maxHeight: 520,
              overflowY: 'auto',
              p: 2,
              borderRadius: 1.5,
              bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa',
              color: theme.palette.mode === 'dark' ? '#c9d1d9' : '#24292f',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {output || (
              <Typography
                component="span"
                sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', fontFamily: 'inherit', fontSize: 'inherit' }}
              >
                Output erscheint hier live...
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block', textAlign: 'right' }}
          >
            {output.length} Zeichen
          </Typography>
        </Paper>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchCyberChefPage;
